import { useState } from 'react';
import { Clock, Fuel, Zap, ShieldAlert, Siren, Users, BarChart3, PlugZap, Wrench, Sparkles, ArrowUpDown, Cog, ShieldCheck, Home, HardHat, Hammer, MapPin, AlertTriangle, ChevronDown, ChevronUp } from 'lucide-react';
import type { Report, ReportAttachment } from '../types';
import { formatDate, formatDateTime, parseResidents } from '../lib/format';
import { Modal, StatusBadge, Avatar } from './ui';
import ReportAttachments from './ReportAttachments';
import { REPORT_TYPE_LABELS } from './ReportFormModal';

const typeIcon = (reportType: string) => {
  if (reportType === 'diesel') return <Fuel className="h-5 w-5 text-amber-600" />;
  if (reportType === 'ekedc') return <Zap className="h-5 w-5 text-orange-600" />;
  if (reportType === 'security_breach') return <ShieldAlert className="h-5 w-5 text-red-600" />;
  if (reportType === 'incident') return <Siren className="h-5 w-5 text-red-600" />;
  if (reportType === 'residents_complaint') return <Users className="h-5 w-5 text-primary" />;
  if (reportType === 'monthly_kpi') return <BarChart3 className="h-5 w-5 text-green-600" />;
  if (reportType === 'electrical') return <PlugZap className="h-5 w-5 text-amber-600" />;
  if (reportType === 'plumbing') return <Wrench className="h-5 w-5 text-primary" />;
  if (reportType === 'cleaning') return <Sparkles className="h-5 w-5 text-teal-600" />;
  if (reportType === 'lift') return <ArrowUpDown className="h-5 w-5 text-primary" />;
  if (reportType === 'generator') return <Cog className="h-5 w-5 text-lime-600" />;
  if (reportType === 'staff_report') return <HardHat className="h-5 w-5 text-primary" />;
  if (reportType === 'residents_report') return <Home className="h-5 w-5 text-violet-600" />;
  if (reportType === 'maintenance') return <Hammer className="h-5 w-5 text-yellow-600" />;
  if (reportType === 'security') return <ShieldCheck className="h-5 w-5 text-rose-600" />;
  return null;
};

interface SectionData {
  label: string;
  lines: string[];
  attachmentIndices: number[];
}

function parseDailySections(content: string, attachments: ReportAttachment[]): SectionData[] {
  const sections: SectionData[] = [];
  const sectionMap = new Map<string, SectionData>();

  const headerRegex = /^\[([^\]]+)\]\s*$/gm;
  let match: RegExpExecArray | null;
  const headerPositions: { label: string; start: number; end: number }[] = [];

  while ((match = headerRegex.exec(content)) !== null) {
    headerPositions.push({ label: match[1], start: match.index + match[0].length, end: -1 });
  }

  for (let i = 0; i < headerPositions.length; i++) {
    headerPositions[i].end = i + 1 < headerPositions.length ? headerPositions[i + 1].start - 1 : content.length;
  }

  for (const hp of headerPositions) {
    const body = content.slice(hp.start, hp.end).trim();
    const lines = body.split('\n').filter((l) => l.trim());
    const data: SectionData = { label: hp.label, lines, attachmentIndices: [] };
    sections.push(data);
    sectionMap.set(hp.label.toLowerCase(), data);
  }

  attachments.forEach((att, idx) => {
    const prefixMatch = att.filename.match(/^\[([^\]]+)\]\s*/);
    if (prefixMatch) {
      const sectionLabel = prefixMatch[1].toLowerCase();
      const section = sectionMap.get(sectionLabel);
      if (section) {
        section.attachmentIndices.push(idx);
      }
    }
  });

  if (sections.length === 0 && content.trim()) {
    sections.push({ label: 'Report', lines: content.split('\n').filter((l) => l.trim()), attachmentIndices: attachments.map((_, i) => i) });
  }

  return sections;
}

function DailySectionBlock({ section, allAttachments, onAttachmentDeleted }: {
  section: SectionData;
  allAttachments: ReportAttachment[];
  onAttachmentDeleted?: (id: number) => void;
}) {
  const [open, setOpen] = useState(true);
  const sectionAttachments = section.attachmentIndices.map((i) => allAttachments[i]).filter(Boolean);

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-4 py-2.5 bg-gray-50 hover:bg-gray-100 transition text-left"
      >
        <span className="flex items-center gap-2 text-sm font-medium text-gray-800">
          {section.label}
          {section.lines.length > 0 && <span className="w-1.5 h-1.5 rounded-full bg-primary inline-block" />}
          {sectionAttachments.length > 0 && (
            <span className="text-xs text-gray-400">({sectionAttachments.length} file{sectionAttachments.length !== 1 ? 's' : ''})</span>
          )}
        </span>
        {open ? <ChevronUp className="h-4 w-4 text-gray-400" /> : <ChevronDown className="h-4 w-4 text-gray-400" />}
      </button>
      {open && (
        <div className="px-4 py-3 bg-white">
          {section.lines.length > 0 ? (
            <div className="space-y-1 mb-3">
              {section.lines.map((line, i) => {
                const colonIdx = line.indexOf(':');
                if (colonIdx > 0 && colonIdx < 80) {
                  const key = line.slice(0, colonIdx).trim();
                  const val = line.slice(colonIdx + 1).trim();
                  return (
                    <div key={i} className="flex gap-2 text-sm">
                      <span className="font-medium text-gray-700 shrink-0">{key}:</span>
                      <span className="text-gray-600">{val}</span>
                    </div>
                  );
                }
                return <p key={i} className="text-sm text-gray-600">{line}</p>;
              })}
            </div>
          ) : (
            <p className="text-sm text-gray-400 italic mb-3">No details provided.</p>
          )}
          {sectionAttachments.length > 0 && (
            <ReportAttachments attachments={sectionAttachments} onDeleted={onAttachmentDeleted} />
          )}
        </div>
      )}
    </div>
  );
}

interface Props {
  report: Report | null;
  onClose: () => void;
  onAttachmentDeleted?: (attachmentId: number) => void;
}

export default function ReportDetailModal({ report, onClose, onAttachmentDeleted }: Props) {
  if (!report) return null;

  const r = report;
  const isStructuredReport = (r.report_type === 'daily' && r.heading === 'Daily Report') || (r.report_type === 'monthly' && r.heading === 'Monthly Report');
  const sections = isStructuredReport ? parseDailySections(r.content, r.attachments) : null;

  return (
    <Modal open={!!report} onClose={onClose} title={r.heading || REPORT_TYPE_LABELS[r.report_type] || 'Report'} maxWidth="max-w-3xl">
      <div className="space-y-5">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-center">
            <Avatar src={r.image_url} name={r.user_name} className="w-10 h-10 mr-3" />
            <div>
              <p className="font-semibold text-gray-800">{r.user_name}</p>
              <p className="text-xs text-gray-500 capitalize">{r.user_role}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <StatusBadge status={r.report_type} />
            {r.facility && (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                <MapPin className="h-3 w-3 mr-1" /> {r.facility}
              </span>
            )}
            {r.estate && (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                <Home className="h-3 w-3 mr-1" /> {r.estate}
              </span>
            )}
            {r.category && r.category !== 'general' && (
              <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600 capitalize">{r.category}</span>
            )}
          </div>
        </div>

        <div className="flex items-center text-xs text-gray-400">
          <Clock className="h-3.5 w-3.5 mr-1" />
          Report date {formatDate(r.report_date)} · Uploaded {formatDateTime(r.created_at)}
        </div>

        <div className="flex flex-wrap items-center gap-3 text-sm">
          {typeIcon(r.report_type)}
          {r.diesel_used_month !== null && (
            <span className="text-gray-700"><span className="font-medium">Diesel used this month:</span> {r.diesel_used_month} L</span>
          )}
          {r.diesel_liters_remaining !== null && (
            <span className="text-gray-700"><span className="font-medium">Diesel remaining:</span> {r.diesel_liters_remaining} L</span>
          )}
          {r.ekedc_used_month !== null && (
            <span className="text-gray-700"><span className="font-medium">EKEDC used this month:</span> {r.ekedc_used_month} kWh</span>
          )}
          {r.ekedc_units_remaining !== null && (
            <span className="text-gray-700"><span className="font-medium">EKEDC units remaining:</span> {r.ekedc_units_remaining} kWh</span>
          )}
          {r.resident_name && (() => {
            const residentEntries = parseResidents(r.resident_name, r.unit);
            return residentEntries.length > 0 && (
              <span className="text-gray-700">
                <span className="font-medium">Residents:</span>{' '}
                {residentEntries.map((re) => `${re.name}${re.unit ? ` (${re.unit})` : ''}`).join(', ')}
              </span>
            );
          })()}
          {r.severity && (
            <span className="inline-flex items-center text-gray-700">
              <AlertTriangle className="h-4 w-4 mr-1 text-red-500" /> Severity:
            </span>
          )}
          {r.severity && <StatusBadge status={r.severity} />}
          {r.security_location && (
            <span className="text-gray-500 text-xs"><span className="font-medium">Location:</span> {r.security_location}</span>
          )}
        </div>

        {sections && sections.length > 0 ? (
          <div className="space-y-2">
            {sections.map((sec) => (
              <DailySectionBlock
                key={sec.label}
                section={sec}
                allAttachments={r.attachments}
                onAttachmentDeleted={onAttachmentDeleted}
              />
            ))}
          </div>
        ) : (
          <>
            <div className="bg-gray-50 border border-gray-100 rounded-lg p-4">
              <p className="text-sm text-gray-700 whitespace-pre-line">{r.content}</p>
            </div>

            {r.action_taken && (
              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-1">Action taken</h4>
                <p className="text-sm text-gray-600 whitespace-pre-line">{r.action_taken}</p>
              </div>
            )}

            {r.attachments.length > 0 && (
              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-2">Attachments</h4>
                <ReportAttachments attachments={r.attachments} onDeleted={onAttachmentDeleted} />
              </div>
            )}
          </>
        )}
      </div>
    </Modal>
  );
}
